import {
  Job,
  PrinterSlotList,
  SPOOL_STATUS_LABELS,
  Spool,
  SpoolDetail,
  type SpoolMovement,
  WeighResult,
  formatDate,
} from '@deizi/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { AffixInput } from '../../components/Form';
import { Icon } from '../../components/Icon';
import { ErrorBox, Skeleton } from '../../components/States';
import { useToast } from '../../components/Toast';
import { ApiClientError, api } from '../../lib/api';
import { usePrinters } from '../../lib/catalog-api';
import {
  formatG,
  useInvalidateInventory,
  usePrinterSlots,
  useSpool,
} from '../../lib/inventory-api';
import { errorMessage } from '../../lib/messages';
import { useInvalidateQueue } from '../../lib/queue-api';
import { useDebounced } from '../../lib/use-debounced';
import { Panel, QrImage, QrScanSheet, spoolLabelUrl } from './qr';
import { SPOOL_BADGE, spoolStatusText } from './spool-common';
import { Combobox } from '../../components/Combobox';
import { printerOption } from '../../components/pickers';

const MOVE_LABELS: Record<SpoolMovement['kind'], string> = {
  purchase: 'Compra',
  job_done: 'Impressão concluída',
  job_failed: 'Impressão com falha',
  weigh_in: 'Pesagem · correção',
  opened: 'Aberto',
  loaded: 'Carregado',
  unloaded: 'Tirado da impressora',
  emptied: 'Marcado como vazio',
  adjust: 'Ajuste',
};

/** `/c/C-0012` (what the QR label opens) → the spool page. */
export function SpoolByCode() {
  const { code } = useParams();
  const found = useQuery({
    queryKey: ['spools', 'code', code],
    queryFn: () => api.get(`/spools/by-code/${encodeURIComponent(code ?? '')}`, SpoolDetail),
    retry: false,
  });
  if (found.data) return <Navigate to={`/carreteis/${found.data.id}`} replace />;
  if (found.isError) {
    return (
      <ErrorBox
        title={`Carretel ${code ?? ''} não encontrado`}
        onRetry={() => void found.refetch()}
      />
    );
  }
  return <Skeleton rows={2} height={120} />;
}

function WeighCard({ spool }: { spool: Spool }) {
  const [gross, setGross] = useState('');
  const toast = useToast();
  const invalidate = useInvalidateInventory();
  const grams = /^\d+([.,]\d)?$/.test(gross.trim()) ? Number(gross.replace(',', '.')) : null;
  const debounced = useDebounced(grams, 300);
  const preview = useQuery({
    queryKey: ['spools', spool.id, 'weigh', debounced],
    queryFn: () =>
      api.post(`/spools/${spool.id}/weigh`, WeighResult, { grossGrams: debounced, confirm: false }),
    enabled: debounced !== null,
    retry: false,
  });
  const apply = useMutation({
    mutationFn: () =>
      api.post(`/spools/${spool.id}/weigh`, WeighResult, { grossGrams: grams, confirm: true }),
    onSuccess: (r) => {
      invalidate();
      setGross('');
      toast(
        r.applied
          ? `${spool.code} corrigido para ${formatG(r.filamentGrams)}.`
          : 'Peso confere. Nada a corrigir.',
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });
  const p = grams !== null && grams === debounced ? preview.data : undefined;
  return (
    <section className="card col" style={{ gap: 10 }}>
      <h2 style={{ fontSize: 18 }}>Pesar na balança</h2>
      <div className="field">
        <label htmlFor="wg">Peso bruto (carretel + filamento)</label>
        <AffixInput id="wg" unit="g" placeholder="0" value={gross} onChange={setGross} />
      </div>
      {preview.isError && grams === debounced ? (
        <p className="help err">
          {preview.error instanceof ApiClientError &&
          JSON.stringify(preview.error.details ?? '').includes('grossGrams')
            ? preview.error.message
            : errorMessage(preview.error)}
        </p>
      ) : null}
      {p ? (
        <div>
          <div className="kv sm">
            <span className="muted">Carretel vazio{p.emptySpoolDefault ? ' (padrão)' : ''}</span>
            <span className="v">− {formatG(p.emptySpoolGrams)}</span>
          </div>
          <div className="kv sm">
            <span className="muted">Filamento pesado</span>
            <span className="v">{formatG(p.filamentGrams)}</span>
          </div>
          <div className="kv sm">
            <span className="muted">Sistema dizia</span>
            <span className="v">{formatG(p.systemGrams)}</span>
          </div>
          <div className="kv sm">
            <span className="muted">Diferença</span>
            <span
              className="v"
              style={{
                color: p.diffGrams > 0 ? 'var(--ok)' : p.diffGrams < 0 ? 'var(--err)' : undefined,
              }}
            >
              {p.diffGrams > 0 ? '+' : p.diffGrams < 0 ? '−' : ''}
              {formatG(Math.abs(p.diffGrams))}
            </span>
          </div>
        </div>
      ) : null}
      <button
        type="button"
        className="btn p block"
        disabled={!p || apply.isPending}
        onClick={() => apply.mutate()}
      >
        {!p
          ? 'Digite o peso bruto'
          : p.diffGrams === 0
            ? 'Peso confere'
            : `Corrigir para ${formatG(p.filamentGrams)}`}
      </button>
      {p?.emptySpoolDefault ? (
        <p className="xs muted">
          Este filamento não tem o peso do carretel vazio cadastrado; usamos{' '}
          {formatG(p.emptySpoolGrams)}. Ajuste em Filamentos.
        </p>
      ) : null}
    </section>
  );
}

function LoadPanel({ spool, onClose }: { spool: Spool; onClose: () => void }) {
  const printers = usePrinters();
  const live = printers.data?.data ?? [];
  const [printerId, setPrinterId] = useState<string | undefined>(
    spool.location?.printerId ?? live[0]?.id,
  );
  const slots = usePrinterSlots(printerId ?? live[0]?.id);
  const invalidate = useInvalidateInventory();
  const toast = useToast();
  const load = useMutation({
    mutationFn: (slot: string) =>
      api.post(`/printers/${printerId ?? live[0]?.id}/slots/${slot}/load`, PrinterSlotList, {
        spoolId: spool.id,
      }),
    onSuccess: (_r, slot) => {
      invalidate();
      const was = slots.data?.data.find((s) => s.slot === slot)?.spool;
      toast(
        `${spool.code} carregado no ${slot === 'ext' ? 'externo' : `slot ${slot}`}${was && was.id !== spool.id ? `; ${was.code} voltou para a prateleira` : ''}.`,
      );
      onClose();
    },
    onError: (err) => toast(errorMessage(err)),
  });
  return (
    <Panel label="Carregar na impressora" onClose={onClose}>
      {live.length > 1 ? (
        <Combobox
          ariaLabel="Impressora"
          value={printerId}
          options={live.map(printerOption)}
          onChange={setPrinterId}
        />
      ) : null}
      <p className="sm muted">Em qual slot? Quem estiver lá volta para a prateleira.</p>
      {(slots.data?.data ?? []).map((s) => (
        <button
          key={s.slot}
          type="button"
          className="listrow"
          style={{ width: '100%' }}
          disabled={load.isPending}
          onClick={() => load.mutate(s.slot)}
        >
          <span className="grow">
            {s.label} · {s.nozzle === 'main' ? 'bico principal' : 'bico auxiliar'}
          </span>
          <span className="sm muted">
            {s.spool?.id === spool.id ? 'este' : (s.spool?.code ?? 'livre')}
          </span>
        </button>
      ))}
      {slots.data && slots.data.data.length === 1 ? (
        <p className="xs muted">
          Esta impressora não tem AMS cadastrado; dá para informar os slots em Impressoras.
        </p>
      ) : null}
    </Panel>
  );
}

/** Spool page (board Carretel-Celular). */
export function SpoolPage() {
  const { id } = useParams();
  const spool = useSpool(id);
  const toast = useToast();
  const navigate = useNavigate();
  const invalidate = useInvalidateInventory();
  const invalidateQueue = useInvalidateQueue();
  const [panel, setPanel] = useState<'actions' | 'load' | 'empty' | 'scan' | null>(null);
  const act = useMutation({
    mutationFn: (action: 'open' | 'empty' | 'unload') =>
      action === 'unload'
        ? api
            .post(
              `/printers/${spool.data!.location!.printerId}/slots/${spool.data!.location!.slot}/load`,
              PrinterSlotList,
              {
                spoolId: null,
              },
            )
            .then(() => null)
        : api.post(`/spools/${id}/${action}`, Spool).then(() => null),
    onSuccess: (_r, action) => {
      invalidate();
      setPanel(null);
      const code = spool.data?.code ?? '';
      toast(
        action === 'open'
          ? `${code} aberto.`
          : action === 'empty'
            ? `${code} marcado como vazio.`
            : `${code} voltou para a prateleira.`,
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });

  const switchSpool = useMutation({
    mutationFn: (n: NonNullable<SpoolDetail['shortFor']>) =>
      api.patch(`/jobs/${n.jobId}`, Job, {
        spools: [{ lineId: n.lineId, spoolId: n.fix!.spoolId }],
      }),
    onSuccess: (j, n) => {
      invalidate();
      void invalidateQueue();
      toast(`${j.number} vai usar ${n.fix!.spoolCode}.`);
    },
    onError: (err) => toast(errorMessage(err)),
  });

  if (spool.isPending) return <Skeleton rows={3} height={120} />;
  if (spool.isError) {
    return (
      <ErrorBox title="Não foi possível carregar o carretel" onRetry={() => void spool.refetch()} />
    );
  }
  const s = spool.data;
  const pct = s.initialGrams > 0 ? Math.round((s.remainingGrams / s.initialGrams) * 100) : 0;
  return (
    <div className="col" style={{ gap: 14, maxWidth: 620, margin: '0 auto', width: '100%' }}>
      <div className="row" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn icon g"
          aria-label="Voltar"
          onClick={() => void navigate(-1)}
        >
          <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}>
            <Icon name="chevronRight" />
          </span>
        </button>
        <div className="grow" style={{ minWidth: 0 }}>
          <p className="xs muted">Carretel {s.code}</p>
          <h1 style={{ fontSize: 22 }} className="trunc">
            {s.filament.name}
          </h1>
        </div>
        <button
          type="button"
          className="btn icon"
          aria-label="Ler QR"
          onClick={() => setPanel('scan')}
        >
          <Icon name="search" />
        </button>
      </div>

      <section className="card col" style={{ gap: 10 }}>
        <div className="row wrap" style={{ gap: 8 }}>
          <button
            type="button"
            className={SPOOL_BADGE[s.status]}
            style={{ cursor: 'pointer', border: 0 }}
            aria-label="Mudar situação"
            onClick={() => setPanel('actions')}
          >
            {spoolStatusText(s)} ▾
          </button>
          {s.low ? <span className="badge warn">Estoque baixo</span> : null}
        </div>
        <p className="fr tab" style={{ fontSize: 36 }}>
          {formatG(s.remainingGrams)}
        </p>
        <p className="sm muted">
          de {formatG(s.initialGrams)} · {pct}%
          {s.purchase
            ? ` · comprado em ${formatDate(s.purchase.purchasedOn + 'T12:00:00Z')}${s.purchase.supplier ? ` · ${s.purchase.supplier}` : ''}`
            : ''}
        </p>
        <div className="layers thin">
          <div className="fill" style={{ width: `${pct}%` }} />
          <div className="nozzle" style={{ left: `${pct}%` }} />
        </div>
        {s.shortFor && s.status !== 'empty' ? (
          <div className="alert warn">
            <Icon name="alert" className="ic" />
            <div className="grow col" style={{ gap: 6 }}>
              <span className="sm">
                Não dá para a próxima impressão da fila: <b>{s.shortFor.label}</b>{' '}
                {s.shortFor.units > 1 ? `placa com ${s.shortFor.units} unidades ` : ''}precisa de{' '}
                {formatG(s.shortFor.needGrams)} ({formatG(s.shortFor.estGrams)} + 5% de folga).
              </span>
              {s.shortFor.fix ? (
                <button
                  type="button"
                  className="btn sm"
                  style={{ alignSelf: 'flex-start' }}
                  disabled={switchSpool.isPending}
                  onClick={() => switchSpool.mutate(s.shortFor!)}
                >
                  Usar {s.shortFor.fix.spoolCode} na fila
                </button>
              ) : (
                <Link className="btn sm" style={{ alignSelf: 'flex-start' }} to="/fila">
                  Ver a fila
                </Link>
              )}
            </div>
          </div>
        ) : null}
      </section>

      {s.status !== 'empty' ? <WeighCard spool={s} /> : null}

      <section className="card row" style={{ gap: 14, alignItems: 'center' }}>
        <QrImage text={spoolLabelUrl(s.code)} size={96} />
        <div className="grow col" style={{ gap: 4 }}>
          <b>{s.code}</b>
          <span className="sm">{s.filament.name}</span>
          <span className="xs muted">
            {formatG(s.initialGrams)}
            {s.purchase ? ` · ${formatDate(s.purchase.purchasedOn + 'T12:00:00Z')}` : ''}
          </span>
          <Link
            className="btn sm"
            style={{ alignSelf: 'flex-start' }}
            to={`/carreteis/etiquetas?ids=${s.id}`}
          >
            Imprimir etiqueta
          </Link>
        </div>
      </section>

      <section className="card col" style={{ gap: 0 }}>
        <h2 style={{ fontSize: 18, marginBottom: 6 }}>Movimentos</h2>
        {s.movements.map((m) => (
          <div key={m.id} className="kv sm">
            <span>
              {MOVE_LABELS[m.kind]}
              {m.note ? ` · ${m.note}` : ''}
              <span className="xs muted" style={{ display: 'block' }}>
                {formatDate(m.at)} · {m.actor}
              </span>
            </span>
            <span
              className="v"
              style={{ color: m.grams < 0 ? 'var(--err)' : m.grams > 0 ? 'var(--ok)' : undefined }}
            >
              {m.grams > 0 ? '+' : m.grams < 0 ? '−' : ''}
              {m.grams ? formatG(Math.abs(m.grams)) : '—'}
            </span>
          </div>
        ))}
      </section>

      {panel === 'actions' ? (
        <Panel
          label={`${s.code} · ${SPOOL_STATUS_LABELS[s.status]}`}
          onClose={() => setPanel(null)}
        >
          {s.status === 'sealed' ? (
            <button type="button" className="btn block" onClick={() => act.mutate('open')}>
              Abrir (tirar o lacre)
            </button>
          ) : null}
          {s.status !== 'empty' ? (
            <button type="button" className="btn block" onClick={() => setPanel('load')}>
              {s.status === 'loaded' ? 'Trocar de slot' : 'Carregar na impressora…'}
            </button>
          ) : null}
          {s.status === 'loaded' ? (
            <button type="button" className="btn block" onClick={() => act.mutate('unload')}>
              Tirar da impressora (volta para a prateleira)
            </button>
          ) : null}
          {s.status !== 'empty' ? (
            <button type="button" className="btn d block" onClick={() => setPanel('empty')}>
              Marcar como vazio
            </button>
          ) : null}
        </Panel>
      ) : null}
      {panel === 'empty' ? (
        <Panel label={`Marcar ${s.code} como vazio?`} onClose={() => setPanel(null)}>
          <p className="sm">
            {s.remainingGrams > 0
              ? `Os ${formatG(s.remainingGrams)} que o sistema ainda conta saem do estoque como sobra.`
              : 'Ele sai das listas de carretéis disponíveis.'}
          </p>
          <button
            type="button"
            className="btn d block"
            disabled={act.isPending}
            onClick={() => act.mutate('empty')}
          >
            Marcar vazio
          </button>
          <button type="button" className="btn block" onClick={() => setPanel(null)}>
            Cancelar
          </button>
        </Panel>
      ) : null}
      {panel === 'load' ? <LoadPanel spool={s} onClose={() => setPanel(null)} /> : null}
      {panel === 'scan' ? <QrScanSheet onClose={() => setPanel(null)} /> : null}
    </div>
  );
}
